import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { formatCurrency, duplicateInvoice } from '../utils/invoiceUtils';

export default function Invoices({ userRole }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeClient = searchParams.get('client') || '';

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        let q;

        // If admin, fetch all invoices, otherwise only user's invoices
        if (userRole === 'admin') {
          q = query(
            collection(db, 'invoices'),
            orderBy('createdAt', 'desc')
          );
        } else {
          q = query(
            collection(db, 'invoices'),
            where('userId', '==', auth.currentUser.uid),
            orderBy('createdAt', 'desc')
          );
        }

        const invoiceSnapshot = await getDocs(q);
        const invoiceList = invoiceSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setInvoices(invoiceList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [userRole]);

  const filteredInvoices = activeClient
    ? invoices.filter(invoice =>
        (invoice.clientName || invoice.customerName || '').toLowerCase() === activeClient.toLowerCase()
      )
    : invoices;

  const handleDuplicate = async (invoice) => {
    setDuplicatingId(invoice.id);
    try {
      const result = await duplicateInvoice(invoice);
      navigate(`/invoice/${result.id}`);
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      alert('Failed to duplicate invoice. Please try again.');
      setDuplicatingId(null);
    }
  };

  if (loading) return (
    <div className="invoices-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <h1 className="invoices-title">
          {activeClient ? `Invoices for ${activeClient}` : 'Invoices'}
        </h1>
        <Link 
          to="/create-invoice" 
          className="create-invoice-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
          Create New Invoice
        </Link>
      </div>

      {activeClient && (
        <div className="filter-banner">
          <span>Showing invoices for <strong>{activeClient}</strong></span>
          <button
            onClick={() => setSearchParams({})}
            className="filter-banner-clear"
          >
            Clear filter
          </button>
        </div>
      )}

      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16">
              <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h13zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-13z"/>
              <path d="M7 5.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0zM7 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0z"/>
            </svg>
          </div>
          <p className="empty-state-text">No invoices found.</p>
          <Link to="/create-invoice" className="empty-state-btn">Create your first invoice</Link>
        </div>
      ) : (
        <div className="invoices-table-container">
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(invoice => (
                <tr key={invoice.id}>
                  <td data-label="Invoice #">
                    <span className="invoice-id">{invoice.invoiceNumber || invoice.invoiceNo}</span>
                  </td>
                  <td data-label="Client">{invoice.clientName || invoice.customerName}</td>
                  <td data-label="Amount">
                    <span className="invoice-amount">{formatCurrency(invoice.total)}</span>
                  </td>
                  <td data-label="Date">
                    {invoice.createdAt && invoice.createdAt.toDate ? 
                      invoice.createdAt.toDate().toLocaleDateString() : 
                      'N/A'}
                  </td>
                  <td data-label="Status">
                    <span className={`invoice-status ${
                      invoice.status === 'paid' 
                        ? 'status-paid' 
                        : invoice.status === 'pending' 
                        ? 'status-pending' 
                        : invoice.status === 'partial'
                        ? 'status-partial'
                        : 'status-overdue'
                    }`}>
                      {invoice.status === 'partial' 
                        ? `Partially Paid (Due: ${formatCurrency(invoice.remainingBalance || 0)})` 
                        : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </td>
                  <td className="responsive-actions-cell" data-label="Actions">
                      <div className="invoice-actions">
                        <Link 
                          to={`/invoice/${invoice.id}`}
                          className="action-btn view-btn"
                        >
                          View
                        </Link>
                        <Link 
                          to={`/edit-invoice/${invoice.id}`}
                          className="action-btn edit-btn"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDuplicate(invoice)}
                          disabled={duplicatingId === invoice.id}
                          className="action-btn duplicate-btn"
                        >
                          {duplicatingId === invoice.id ? '...' : 'Copy'}
                        </button>
                      </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}